import React, { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, MapPin } from "lucide-react";
import LocationsMap from "@/components/LocationsMap";

const locations = [
  {
    id: "ncr",
    region: "Delhi NCR",
    assets: 6,
    venues: [
      { name: "Skyline Golf", count: 1 },
      { name: "Zen Golf", count: 1 },
      { name: "Golden Greens", count: 1 },
      { name: "Classic Golf & Country Club", count: 1 },
      { name: "Hamoni Golf", count: 1 },
      { name: "Noida Golf Course", count: 1 },
    ],
  },
  {
    id: "chandigarh",
    region: "Chandigarh",
    assets: 2,
    venues: [
      { name: "Chandigarh Golf Club", count: 1 },
      { name: "CGA Range Chandigarh", count: 1 },
    ],
  },
  {
    id: "panchkula",
    region: "Panchkula",
    assets: 2,
    venues: [{ name: "Panchkula Golf Club", count: 2 }],
  },
  {
    id: "jaipur",
    region: "Jaipur",
    assets: 1,
    venues: [{ name: "Rambagh Golf Club", count: 1 }],
  },
  {
    id: "ooty",
    region: "Ooty",
    assets: 1,
    venues: [{ name: "Ooty Gymkhana Club", count: 1 }],
  },
];

const ease = [0.22, 1, 0.36, 1];

const Locations = () => {
  const [open, setOpen] = useState("ncr");

  return (
    <section
      id="locations"
      data-testid="locations-section"
      className="relative py-28 md:py-40 bg-ivory-soft"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-16 md:mb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease }}
        >
          <LocationsMap />
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease }}
          className="lg:col-span-5"
        >
          <span className="eyebrow flex items-center gap-3">
            <span className="w-10 h-px bg-brass" />
            Premium Locations
          </span>
          <h2 className="h-display text-4xl md:text-6xl mt-6">
            Eleven venues.
            <br />
            <span className="italic text-brass">One discerning</span> network.
          </h2>
          <p className="mt-8 text-forest-600 text-base md:text-lg leading-relaxed font-light max-w-md">
            Strategically positioned in India's most exclusive golf
            clubs and premium venues. Every screen, an address.
          </p>

          <div className="mt-12 hairline shadow-luxe p-8 bg-ivory" data-testid="network-summary">
            <div className="font-serif text-6xl md:text-7xl text-brass font-light">
              11+
            </div>
            <div className="mt-2 text-xs tracking-[0.25em] uppercase text-forest-500">
              Total Premium Locations
            </div>
            <div className="gold-divider my-6" />
            <div className="text-forest-600 text-sm leading-relaxed">
              Across India's elite golf clubs — with new venues
              joining every quarter.
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease, delay: 0.15 }}
          className="lg:col-span-7"
          data-testid="locations-accordion"
        >
          {locations.map((loc) => {
            const isOpen = open === loc.id;
            return (
              <div
                key={loc.id}
                className="border-b border-forest-800/10"
                data-testid={`location-${loc.id}`}
              >
                <button
                  onClick={() => setOpen(isOpen ? "" : loc.id)}
                  className="w-full flex items-center justify-between py-8 text-left group"
                  data-testid={`location-toggle-${loc.id}`}
                >
                  <div className="flex items-center gap-5">
                    <span className="font-serif italic text-brass/40 text-xl w-8">
                      0{locations.indexOf(loc) + 1}
                    </span>
                    <h3 className="font-serif text-3xl md:text-5xl text-forest-900 group-hover:text-brass transition-colors duration-500">
                      {loc.region}
                    </h3>
                  </div>
                  <div className="flex items-center gap-6">
                    <span className="hidden md:inline-block text-xs tracking-[0.2em] uppercase text-brass border border-brass/40 px-3 py-1.5">
                      {loc.assets} {loc.assets === 1 ? "Asset" : "Assets"}
                    </span>
                    <ChevronDown
                      size={22}
                      strokeWidth={1.4}
                      className={`text-forest-500 transition-transform duration-500 ${
                        isOpen ? "rotate-180 text-brass" : ""
                      }`}
                    />
                  </div>
                </button>
                <motion.div
                  initial={false}
                  animate={{
                    height: isOpen ? "auto" : 0,
                    opacity: isOpen ? 1 : 0,
                  }}
                  transition={{ duration: 0.6, ease }}
                  className="overflow-hidden"
                >
                  <div className="pb-10 pl-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {loc.venues.map((v) => (
                      <div
                        key={v.name}
                        className="flex items-center justify-between hairline px-5 py-4 bg-ivory"
                        data-testid={`venue-${loc.id}-${v.name.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <div className="flex items-center gap-3">
                          <MapPin
                            size={16}
                            strokeWidth={1.4}
                            className="text-brass"
                          />
                          <span className="text-forest-800 text-sm">{v.name}</span>
                        </div>
                        <span className="text-xs text-forest-500 tracking-wider">
                          {v.count} {v.count === 1 ? "Asset" : "Assets"}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Locations;
